import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, TrendingUp, Clock, Heart, Brain, Zap, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessibleCategories } from '@/hooks/useAccessibleCategories';
import { backendClient } from '@/lib/backendClient';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface Recommendation {
  id: string;
  type: 'category' | 'mood-based' | 'time-based' | 'streak' | 'discovery';
  title: string;
  description: string;
  categoryId: string;
  categoryName: string;
  reason: string;
  confidence: number;
  icon: React.ElementType;
  gradient: string;
  badge?: string;
}

interface SmartRecommendationsPanelProps {
  className?: string;
  maxRecommendations?: number;
  showHeader?: boolean;
}

const SmartRecommendationsPanel: React.FC<SmartRecommendationsPanelProps> = ({
  className,
  maxRecommendations = 4,
  showHeader = true
}) => {
  const { user } = useAuth();
  const { accessibleCategories } = useAccessibleCategories();
  const navigate = useNavigate();
  
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [sessionHistory, setSessionHistory] = useState<any[]>([]);

  useEffect(() => {
    if (user && accessibleCategories.length > 0) {
      generateRecommendations();
    }
  }, [user, accessibleCategories]);

  const loadUserData = async () => {
    if (!user) return;

    try {
      // Load user profile
      const { data: profile } = await backendClient
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Load recent session history
      const { data: sessions } = await backendClient
        .from('sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      setUserProfile(profile);
      setSessionHistory(sessions || []);
    } catch (error) {
      console.error('Error loading user data for recommendations:', error);
    }
  };

  const generateRecommendations = async () => {
    setIsLoading(true);
    await loadUserData();

    const recs: Recommendation[] = [];
    const currentHour = new Date().getHours();
    const dayOfWeek = new Date().getDay();

    // Time-based recommendations
    if (currentHour >= 6 && currentHour <= 9) {
      recs.push(createTimeBasedRecommendation('morning'));
    } else if (currentHour >= 12 && currentHour <= 14) {
      recs.push(createTimeBasedRecommendation('midday'));
    } else if (currentHour >= 18 && currentHour <= 22) {
      recs.push(createTimeBasedRecommendation('evening'));
    } else if (currentHour >= 22 || currentHour <= 5) {
      recs.push(createTimeBasedRecommendation('night'));
    }

    // Usage pattern recommendations
    const usagePatterns = analyzeUsagePatterns();
    if (usagePatterns.preferredCategory) {
      recs.push(createUsageBasedRecommendation(usagePatterns.preferredCategory));
    }

    // Discovery recommendations (categories user hasn't tried)
    const discoveryRec = createDiscoveryRecommendation();
    if (discoveryRec) {
      recs.push(discoveryRec);
    }

    // Streak motivation
    const streakRec = createStreakRecommendation();
    if (streakRec) {
      recs.push(streakRec);
    }

    // Mood-based recommendations
    recs.push(...createMoodBasedRecommendations());

    // Sort by confidence and limit
    const sortedRecs = recs
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, maxRecommendations);

    setRecommendations(sortedRecs);
    setIsLoading(false);
  };

  const createTimeBasedRecommendation = (timeOfDay: string): Recommendation => {
    const timeMap = {
      morning: {
        categoryId: 'motivation',
        title: 'Start Your Day Strong',
        description: 'Energize yourself with a motivation session',
        reason: 'Perfect for morning energy',
        icon: Zap,
        gradient: 'bg-gradient-secondary'
      },
      midday: {
        categoryId: 'be_still',
        title: 'Midday Reset',
        description: 'Take a peaceful break to recharge',
        reason: 'Ideal for midday restoration',
        icon: Heart,
        gradient: 'bg-gradient-accent'
      },
      evening: {
        categoryId: 'meditation',
        title: 'Evening Unwind',
        description: 'Release the day\'s stress with meditation',
        reason: 'Perfect for evening relaxation',
        icon: Brain,
        gradient: 'bg-gradient-primary'
      },
      night: {
        categoryId: 'sleep',
        title: 'Peaceful Sleep',
        description: 'Drift into restful sleep naturally',
        reason: 'Designed for bedtime',
        icon: Heart,
        gradient: 'bg-gradient-accent'
      }
    };

    const config = timeMap[timeOfDay as keyof typeof timeMap];
    const category = accessibleCategories.find(c => c.id === config.categoryId);

    return {
      id: `time-${timeOfDay}`,
      type: 'time-based',
      ...config,
      categoryName: category?.name || config.title,
      confidence: 8,
      badge: 'Perfect Timing'
    };
  };

  const analyzeUsagePatterns = () => {
    if (!sessionHistory.length) return {};

    const categoryCount: Record<string, number> = {};
    sessionHistory.forEach(session => {
      categoryCount[session.category_id] = (categoryCount[session.category_id] || 0) + 1;
    });

    const preferredCategory = Object.entries(categoryCount)
      .sort(([,a], [,b]) => b - a)[0]?.[0];

    return { preferredCategory };
  };

  const createUsageBasedRecommendation = (categoryId: string): Recommendation => {
    const category = accessibleCategories.find(c => c.id === categoryId);
    if (!category) return null as any;

    return {
      id: `usage-${categoryId}`,
      type: 'category',
      title: `More ${category.name}`,
      description: 'Continue with your favorite wellness practice',
      categoryId,
      categoryName: category.name,
      reason: 'Based on your preferences',
      confidence: 7,
      icon: TrendingUp,
      gradient: 'bg-gradient-primary',
      badge: 'Your Favorite'
    };
  };

  const createDiscoveryRecommendation = (): Recommendation | null => {
    const triedCategories = new Set(sessionHistory.map(s => s.category_id));
    const untriedCategories = accessibleCategories.filter(c => !triedCategories.has(c.id));
    
    if (untriedCategories.length === 0) return null;

    const randomCategory = untriedCategories[Math.floor(Math.random() * untriedCategories.length)];

    return {
      id: `discovery-${randomCategory.id}`,
      type: 'discovery',
      title: `Discover ${randomCategory.name}`,
      description: randomCategory.description,
      categoryId: randomCategory.id,
      categoryName: randomCategory.name,
      reason: 'New experience awaits',
      confidence: 6,
      icon: Sparkles,
      gradient: 'bg-gradient-hero',
      badge: 'New'
    };
  };

  const createStreakRecommendation = (): Recommendation | null => {
    const recentSessions = sessionHistory.filter(s => {
      const sessionDate = new Date(s.created_at);
      const daysDiff = (Date.now() - sessionDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysDiff <= 7;
    });

    if (recentSessions.length < 2) return null;

    return {
      id: 'streak-motivation',
      type: 'streak',
      title: 'Keep Your Streak Going!',
      description: `You've had ${recentSessions.length} sessions this week`,
      categoryId: 'motivation',
      categoryName: 'Motivation',
      reason: 'Maintain your momentum',
      confidence: 9,
      icon: TrendingUp,
      gradient: 'bg-gradient-secondary',
      badge: `${recentSessions.length} This Week`
    };
  };

  const createMoodBasedRecommendations = (): Recommendation[] => {
    const moodRecommendations = [
      {
        id: 'mood-stress',
        title: 'Feeling Stressed?',
        description: 'Find calm with anxiety-focused sessions',
        categoryId: 'anxiety',
        reason: 'For stress relief',
        confidence: 5
      },
      {
        id: 'mood-energy',
        title: 'Need Energy?',
        description: 'Boost your motivation and drive',
        categoryId: 'motivation', 
        reason: 'For increased energy',
        confidence: 5
      }
    ];

    return moodRecommendations.map(mood => {
      const category = accessibleCategories.find(c => c.id === mood.categoryId);
      return {
        ...mood,
        type: 'mood-based' as const,
        categoryName: category?.name || mood.title,
        icon: Heart,
        gradient: 'bg-gradient-accent'
      };
    });
  };

  const handleRecommendationClick = (rec: Recommendation) => {
    navigate(`/category/${rec.categoryId}`);
  };

  if (isLoading) {
    return (
      <Card className={cn('animate-pulse', className)}>
        <CardHeader>
          <div className="h-6 bg-muted rounded w-1/2" />
          <div className="h-4 bg-muted rounded w-3/4" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <Card className={cn('overflow-hidden', className)}>
      {showHeader && (
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            Smart Recommendations
          </CardTitle>
          <CardDescription>
            Personalized suggestions based on your wellness journey
          </CardDescription>
        </CardHeader>
      )}
      
      <CardContent className={showHeader ? 'pt-0' : ''}>
        <div className="space-y-3">
          {recommendations.map((rec) => (
            <button
              key={rec.id}
              onClick={() => handleRecommendationClick(rec)}
              className="w-full p-4 text-left rounded-xl border border-border/50 hover:border-primary/50 hover:bg-gradient-to-r hover:from-primary/5 hover:to-secondary/5 transition-all duration-300 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className={cn(
                    'rounded-lg p-2 flex-shrink-0 transition-transform group-hover:scale-110',
                    rec.gradient
                  )}>
                    <rec.icon className="h-4 w-4 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">
                        {rec.title}
                      </h3>
                      {rec.badge && (
                        <Badge variant="secondary" className="text-xs">
                          {rec.badge}
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {rec.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-primary/70">
                        {rec.reason}
                      </span>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">5-15 min</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartRecommendationsPanel;
